import type { PropsWithChildren } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import type { Configuration } from '@gov-nx/api';
import { useConfigurationQuery } from '@gov-nx/api';
import { useConnection } from '@gov-nx/core/hooks';
import { useConfigurationStore } from '@gov-nx/store';
import { useAppContext } from '../context/AppContext';
import { GovError } from '../error/gov-error';
import { TranslationInitProvider } from './TranslationInitProvider';

interface InitProviderProps extends PropsWithChildren {
	onSuccess?: (configuration: Configuration) => void;
}

export interface InitProviderRef {
	refetch: () => void;
}

export const InitProvider = forwardRef<InitProviderRef, InitProviderProps>(
	({ children, onSuccess }, ref) => {
		const { setAppError } = useAppContext();
		const { isOnline } = useConnection();

		const saveConfiguration = useConfigurationStore('saveConfiguration');
		const { isFetched, refetch } = useConfigurationQuery({
			onError: (e) => {
				isOnline && setAppError(new GovError(e));
			},
			onSuccess: (configuration) => {
				saveConfiguration(configuration);
				onSuccess && onSuccess(configuration);
			},
		});

		useImperativeHandle(
			ref,
			() => ({
				refetch,
			}),
			[refetch]
		);

		if (isFetched) {
			return <TranslationInitProvider>{children}</TranslationInitProvider>;
		}
		return null;
	}
);
